// The editor for a mark: a cue (one or more departments), a standby or a note.

import { useLayoutEffect, useRef } from "preact/hooks";
import { cuesAfter, DocIndex, standbyCoverage, standbyText } from "../../format/derive.ts";
import { insideNoteLayout } from "../../layout/layout.ts";
import { closePanel, deleteOpen, LAYER_SUGGESTIONS, savePanel, suggestThrough, toggleDepartment } from "../actions.ts";
import { closingPanel, doc, ix, type PanelForm, panel } from "../state.ts";
import { chipColors } from "./kit.tsx";
import { reveal } from "../viewer.ts";

const update = (patch: Partial<PanelForm>) => {
  if (panel.value) panel.value = { ...panel.value, ...patch, error: patch.error ?? null };
};

function where(form: PanelForm, index: DocIndex): string {
  const label = index.page(form.anchor.page).label;
  return `p. ${label || form.anchor.page}${label ? ` (PDF ${form.anchor.page})` : ""} · ${form.anchor.shape === "pin" ? "pin" : "region"}`;
}

/** How many upcoming cues the standby editor offers as its last cue. */
const PICK_LIMIT = 40;

function StandbyBody(props: { form: PanelForm }) {
  const d = doc.value;
  if (!d) return null;
  const { form } = props;
  // Work on a copy with this standby where it sits now, so text and choices are live.
  const copy = structuredClone(d);
  const id = form.id || "s-~preview";
  const index = new DocIndex(copy);
  const through = form.through || suggestThrough(index, form.anchor, id);
  copy.standbys = [...copy.standbys.filter((s) => s.id !== id), { id, anchor: form.anchor, through }];
  const live = new DocIndex(copy);
  const info = standbyCoverage(live).find((s) => s.standby.id === id);
  const covered = new Set(info?.covered.map((c) => c.id));
  const text = info ? (standbyText(live, info.covered) ?? "SB —") : "SB —";
  const upcoming = cuesAfter(live, { id, anchor: form.anchor }).slice(0, PICK_LIMIT);
  const page = (cueMoment: string) => live.pageLabel(live.moments.get(cueMoment)?.anchor.page ?? 0);
  return (
    <div class="standby-body">
      <div class={`standby-preview ${covered.size ? "" : "empty"}`}>{text}</div>
      {upcoming.length ? (
        <>
          <p class="hint">Tap the last cue this standby warns. It covers every cue from here down to that one, in every department.</p>
          <div class="through-list" role="listbox" aria-label="Last cue covered">
            {upcoming.map((c) => {
              const dept = live.departments.get(c.department);
              const chosen = c.id === through;
              return (
                <button
                  type="button"
                  role="option"
                  aria-selected={chosen}
                  class={`through-row ${covered.has(c.id) ? "covered" : ""} ${chosen ? "chosen" : ""}`}
                  style={chipColors(dept?.color ?? "#888888")}
                  onClick={() => update({ through: c.id })}
                >
                  <span class="code">{live.cueRef(c)}</span>
                  <span class="label">{c.label}</span>
                  <span class="page">p. {page(c.moment)}</span>
                  <span class="mark" aria-hidden="true">
                    {chosen ? "last ✓" : ""}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <p class="hint warn">No cues come after this spot, so this standby has nothing to warn. Place it above (before) the cues it should call.</p>
      )}
      <p class="hint">You don't type a standby's text: q2cue works it out from the cues it covers, and keeps it right when cues are added in between or renumbered.</p>
    </div>
  );
}

export function Panel() {
  const form = panel.value;
  const index = ix.value;
  const first = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    if (form?.mode === "new") first.current?.focus({ preventScroll: true });
  }, [form?.mode, form?.id, form?.kind]);
  if (!form || !index) return null;
  const insideLayout = form.kind === "note" && form.placement === "inside"
    ? insideNoteLayout(index.page(form.anchor.page), form.anchor, form.text.trim(), "#212121")
    : null;
  const insideMessage = insideLayout?.error ?? insideLayout?.warning;

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") closePanel();
  };
  const kinds: [PanelForm["kind"], string][] = [
    ["cue", "Cue"],
    ["standby", "Standby"],
    ["note", "Note"],
  ];

  return (
    <form
      class="panel"
      autocomplete="off"
      inert={closingPanel.value}
      aria-busy={closingPanel.value}
      onKeyDown={onKey}
      onSubmit={(e) => {
        e.preventDefault();
        savePanel();
      }}
    >
      <div class="panel-head">
        <div class="panel-title">
          <strong>{form.mode === "new" ? "New" : "Edit"}</strong>
          <span class="kind">{form.mode === "edit" && form.kind === "cue"
            ? form.rows.map((r) => `${index.departments.get(r.department)?.code ?? "?"} ${r.number}`.trim()).join(" · ")
            : kinds.find(([k]) => k === form.kind)?.[1]}</span>
        </div>
        {form.mode === "new" ? (
          <div class="segmented" role="tablist">
            {kinds.map(([k, name]) => (
              <button type="button" role="tab" aria-pressed={form.kind === k} onClick={() => update({ kind: k })}>
                {name}
              </button>
            ))}
          </div>
        ) : (
          <button type="button" onClick={() => reveal({ kind: form.kind === "cue" ? "moment" : form.kind, id: form.id }, form.anchor.page)}>
            Show on page
          </button>
        )}
        <span class="where">{where(form, index)}</span>
      </div>
      <div class="panel-body">
      {form.restored && <div class="restored">Restored an edit that wasn't saved before the page reloaded.</div>}

      {form.kind === "cue" && (
        <>
          <div class="chips">
            {index.sortedDepartments.map((d) => (
              <button type="button" class="chip" style={chipColors(d.color)} aria-pressed={form.rows.some((r) => r.department === d.id)} onClick={() => toggleDepartment(d.id)}>
                {d.code}
              </button>
            ))}
          </div>
          {form.rows.map((r, i) => {
            const d = index.departments.get(r.department);
            const set = (patch: Partial<typeof r>) => update({ rows: form.rows.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
            return (
              <div class="cue-row" style={chipColors(d?.color ?? "#888888")}>
                <span class="code">{d?.code ?? "?"}</span>
                <input
                  class="number"
                  aria-label={`${d?.code} cue number`}
                  value={r.number}
                  autocapitalize="characters"
                  autocorrect="off"
                  spellcheck={false}
                  onInput={(e) => set({ number: (e.target as HTMLInputElement).value })}
                />
                <label class="field">
                  Label
                  <textarea
                    ref={i === 0 ? first : undefined}
                    class="label"
                    rows={3}
                    placeholder="Label"
                    aria-label={`${d?.code} label`}
                    value={r.label}
                    onInput={(e) => set({ label: (e.target as HTMLTextAreaElement).value })}
                  />
                </label>
                <label class="field">
                  Cue notes
                  <textarea
                    class="notes"
                    rows={3}
                    placeholder="Cue lists only"
                    aria-label={`${d?.code} cue notes`}
                    value={r.notes}
                    onInput={(e) => set({ notes: (e.target as HTMLTextAreaElement).value })}
                  />
                </label>
              </div>
            );
          })}
          <label class="field">
            Script text
            <textarea rows={3} value={form.scriptText} placeholder="The words where the GO is (optional)" onInput={(e) => update({ scriptText: (e.target as HTMLTextAreaElement).value })} />
          </label>
        </>
      )}

      {form.kind === "standby" && <StandbyBody form={form} />}

      {form.kind === "note" && (
        <>
          <div class="chips">
            {index.sortedLayers.map((l) => (
              <button type="button" class="chip" style={chipColors(l.color)} aria-pressed={form.layer === l.id} onClick={() => update({ layer: l.id, newLayerName: "" })}>
                {l.name}
              </button>
            ))}
            {LAYER_SUGGESTIONS.filter((s) => !index.doc.noteLayers.some((l) => l.name.toLowerCase() === s.name.toLowerCase())).map((s) => (
              <button type="button" class="chip suggested" style={chipColors(s.color)} aria-pressed={!form.layer && form.newLayerName === s.name} onClick={() => update({ layer: null, newLayerName: s.name })}>
                + {s.name}
              </button>
            ))}
          </div>
          {!form.layer && (
            <label class="field">
              New layer
              <input value={form.newLayerName} placeholder="Layer name" onInput={(e) => update({ newLayerName: (e.target as HTMLInputElement).value })} />
            </label>
          )}
          <div class="segmented">
            <button type="button" aria-pressed={form.style === "box"} onClick={() => update({ style: "box" })}>
              Box
            </button>
            <button type="button" aria-pressed={form.style === "strike"} onClick={() => update({ style: "strike", placement: "margin" })}>
              Strike (cut)
            </button>
          </div>
          <div class="field">
            <span>Text placement</span>
            <div class="segmented" role="group" aria-label="Text placement">
              <button type="button" aria-pressed={form.placement !== "inside"} onClick={() => update({ placement: "margin" })}>
                Margin
              </button>
              <button type="button" aria-pressed={form.placement === "inside"} disabled={form.anchor.shape !== "region" || form.style !== "box"} onClick={() => update({ placement: "inside" })}>
                Inside box
              </button>
            </div>
          </div>
          {form.anchor.shape !== "region" || form.style !== "box" ? (
            <p class="hint">Inside-box rewrites need a region with the Box style.</p>
          ) : form.placement === "inside" && (
            <p class="hint">Replaces the covered text with a white overlay and 12 pt text. The source PDF stays unchanged.</p>
          )}
          <label class="field">
            {form.placement === "inside" ? "Replacement text" : "Note"}
            <textarea ref={first} rows={4} value={form.text} onInput={(e) => update({ text: (e.target as HTMLTextAreaElement).value })} />
          </label>
          {insideMessage && <p class="hint warn" role="status">{insideMessage}</p>}
        </>
      )}

      {form.error && <div class="error">{form.error}</div>}
      </div>
      <div class="panel-footer">
        <div class="panel-buttons">
          <button type="button" onClick={closePanel}>
            Cancel
          </button>
          <button type="submit" class="primary">
            Save
          </button>
        </div>
        {form.mode === "edit" && (
          <div class="panel-delete">
            <button type="button" class="danger" onClick={deleteOpen}>
              Delete
            </button>
          </div>
        )}
      </div>
    </form>
  );
}
